/**
 * Authentication context provider with Keycloak OIDC integration,
 * JWT storage, automatic token refresh, role extraction, and
 * VITE_AUTH_DISABLED dev mode.
 *
 * Security: PKCE (RFC 7636) enforced on every authorization request.
 * Refresh token is held in-memory only — never written to any storage.
 * Session recovery on page load uses Keycloak silent check-sso (hidden iframe,
 * prompt=none), requiring a /silent-check-sso.html at the app origin.
 *
 * IMPERSONATION ("view as") SESSION SHELL (SPEC-FRONTEND-RULE-095,
 * TASK-IMP-AUTHCONTEXT-SPA-WIRING). ONE file and never split, because a
 * half-applied change here fails OPEN:
 * - the ORDERED arrival sequence (clause 5): the `?imp=` hint was stashed at
 *   module scope -> the ordinary OIDC bootstrap completes AS THE IMPERSONATOR
 *   -> `GET /api/v1/me/impersonation` through the owning module's one fetcher
 *   -> the descriptor is set and the act-as getter reads it -> `?imp=` is
 *   stripped and the hint consumed -> the workspace selection is cleared ->
 *   ONLY THEN do the children (and PermissionProvider's first fetch) mount;
 * - the descriptor lives in memory only; a token refresh replaces the token
 *   and the TOKEN's user but can never touch the binding, so the act-as mode
 *   survives every refresh tick (design section 4.6);
 * - the shell exposes the EFFECTIVE identity (the target's) as `user`;
 * - the exit ends the binding and NEVER calls logout(); both teardown paths
 *   (logout() and forceLogout()) end any live binding first.
 *
 * @module store/AuthContext
 * @see TASK-MIG-AUTH-HARDEN
 * @see TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-007
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useLingui } from "@lingui/react/macro";
import {
  setTokenGetter,
  setTokenRefresher,
  setTenantIdGetter,
  setWorkspaceIdGetter,
} from "../api/client";
import {
  IMPERSONATION_TERMINAL_EVENT,
  TERMINAL_REASON_END_CODE,
  exitImpersonationSession,
  fetchImpersonationDescriptor,
  publishImpersonationShellSignal,
  safeReturnTo,
  setActAsGetter,
  setImpersonationDiscoveryState,
  type ImpersonationDescriptor,
  type ImpersonationTerminalEventDetail,
} from "../api/impersonation";
import { invalidateWorkspaceCache } from "../api/workspace";
import {
  TENANT_REFUSED_EVENT,
  dispatchTenantRefused,
  tenantRefusalFromBody,
  type TenantRefusal,
  type TenantRefusedEventDetail,
} from "../api/tenantAccess";
import {
  ImpersonationBanner,
  IMPERSONATION_BANNER_MIN_HEIGHT,
  type ImpersonationEndCode,
} from "../components/shared/ImpersonationBanner";
import { EmptyState } from "../components/shared/EmptyState";
import {
  clearImpersonationHint,
  readImpersonationHint,
  urlWithoutImpersonationHint,
} from "../utils/impersonationHint";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  /** Unique user id from Keycloak sub claim. */
  id: string;
  /** Tenant id from JWT tenant_id or organization claim. */
  tenantId: string;
  /** User email. */
  email: string;
  /** Display name. */
  name: string;
  /** Realm and client roles extracted from JWT. */
  roles: string[];
}

export interface AuthState {
  /** Whether the auth module has finished initializing. */
  initialized: boolean;
  /** Whether the user is authenticated. */
  authenticated: boolean;
  /** The current user, or null when unauthenticated. */
  user: AuthUser | null;
  /** Raw access token (JWT). */
  accessToken: string | null;
}

/**
 * Where the impersonation shell is. `none` - no binding; `discovering` - the
 * arrival read is in flight; `active` - a binding is live and armed;
 * `ending` - the exit call is in flight; `error` - the arrival read FAILED
 * (rendered loud, never as "no binding"); `terminal` - the server ended the
 * binding and the banner shows its terminal card.
 */
export type ImpersonationStatus =
  | "none"
  | "discovering"
  | "active"
  | "ending"
  | "error"
  | "terminal";

/** The persistent notice shown when the exit call failed (RULE-095(9)). */
export interface ImpersonationExitFailure {
  /** The binding's own deadline: when it will end regardless. */
  expiresAt: string;
  /** Where the notice's button sends the operator. */
  returnTo: string;
}

export interface AuthContextValue extends AuthState {
  /** Trigger OIDC redirect login with PKCE. */
  login: () => Promise<void>;
  /** Login with username/password via Keycloak ROPC grant. */
  loginWithCredentials: (username: string, password: string) => Promise<void>;
  /** Trigger logout and clear tokens. */
  logout: () => void;
  /** Check whether user has a specific role. */
  hasRole: (role: string) => boolean;
  /** Check whether user has any of the given roles. */
  hasAnyRole: (roles: string[]) => boolean;
  /** Whether SSO (external IdP redirect) is enabled. */
  ssoEnabled: boolean;
  /**
   * Why the last sign-in or session ended when the gateway refused the
   * person's tenant (R-MT-08: suspended or gone); null otherwise. The login
   * page shows it. Cleared by the next successful sign-in.
   */
  signInRefusal?: TenantRefusal | null;
  /**
   * The live act-as descriptor (`AuthContext.impersonation`,
   * frontend_architecture_HLD.md), or null. While it is non-null, `user` is
   * the TARGET and {@link impersonator} is the real signed-in person.
   */
  impersonation: ImpersonationDescriptor | null;
  /** The real signed-in person while a binding is live; null otherwise. */
  impersonator: AuthUser | null;
  /** Where the impersonation shell is. */
  impersonationStatus: ImpersonationStatus;
  /** The banner's end code once the server ended the binding. */
  impersonationTerminalCode?: ImpersonationEndCode;
  /** Set when the exit call failed; drives the persistent notice. */
  impersonationExitFailure: ImpersonationExitFailure | null;
  /**
   * Bumped when the re-check poll reports a stale role re-check, so the
   * permission hook refetches (roles_recheck_ok, RULE-095(8)).
   */
  permissionsEpoch: number;
  /** Exit the view: end the binding, clear local state, go back. Never logs out. */
  exitImpersonation: () => Promise<void>;
  /** The terminal card's and the failure notice's one button: back to return_to. */
  returnFromImpersonation: () => void;
  /** Re-run the arrival read after it failed. */
  retryImpersonationDiscovery: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to access the current auth state and auth actions.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";
const SSO_ENABLED = import.meta.env.VITE_SSO_ENABLED === "true";

const DEV_USER: AuthUser = {
  id: "dev",
  tenantId: "demo-tenant",
  email: "dev@localhost",
  name: "Dev User",
  roles: [],
};

/** sessionStorage key set on explicit logout to suppress silent SSO re-auth. */
const LOGGED_OUT_KEY = "verisa_logged_out";

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1];
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function extractUser(token: string): AuthUser {
  const payload = decodeJwtPayload(token);
  const realmRoles =
    ((payload.realm_access as Record<string, unknown>)
      ?.roles as string[]) ?? [];
  return {
    id: (payload.sub as string) ?? "",
    tenantId: (payload.tenant_id as string) ?? (payload.organization as string) ?? "",
    email: (payload.email as string) ?? "",
    name: (payload.name as string) ?? (payload.preferred_username as string) ?? "",
    roles: realmRoles,
  };
}

// ---------------------------------------------------------------------------
// PKCE helpers (RFC 7636)
// ---------------------------------------------------------------------------

function base64UrlEncode(buffer: Uint8Array): string {
  return btoa(String.fromCharCode(...Array.from(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/** True where SubtleCrypto exists: a secure context (https or localhost). */
function hasSubtleCrypto(): boolean {
  return typeof crypto !== "undefined" && typeof crypto.subtle?.digest === "function";
}

/** The S256 code challenge of `verifier` (RFC 7636 section 4.2). */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

// ---------------------------------------------------------------------------
// Provider Props
// ---------------------------------------------------------------------------

export interface AuthProviderProps {
  children: React.ReactNode;
  /** Keycloak realm URL, e.g. https://auth.example.com/realms/verisa */
  keycloakUrl?: string;
  /** Keycloak client id. */
  clientId?: string;
  /**
   * Run the impersonation arrival read after the OIDC bootstrap. TRUE on the
   * two landing SPAs (tenant, workspace); the admin SPA passes FALSE because
   * it is never a landing surface (target_surface is tenant|workspace only,
   * design section 4.7) and must never arm the act-as mode itself.
   */
  impersonationDiscovery?: boolean;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * The stable, locale-agnostic code of a refused token request. LoginPage maps
 * each code to a Lingui message, so the server's English text is never shown
 * (EN sweep 2026-09-29, B-auth-invite): the gateway relays Keycloak's body
 * unchanged, and Keycloak's direct grant answers `invalid_grant` for every
 * credential problem, telling them apart only in `error_description`. The
 * gateway's own refusals carry a VERISA.* code, and a non-JSON body (a 502/504
 * page) carries nothing.
 */
export function loginErrorCode(status: number, body: Record<string, string | undefined>): string {
  const error = body.error ?? "";
  const description = body.error_description ?? "";
  const tenantRefusal = tenantRefusalFromBody(body);
  if (tenantRefusal) return tenantRefusal;
  if (error === "user_disabled") return "auth/account-disabled";
  if (error === "invalid_grant") {
    if (description.includes("not fully set up")) return "auth/account-requires-password-change";
    if (description === "Account disabled") return "auth/account-disabled";
    if (description.includes("temporarily disabled")) return "auth/account-temporarily-locked";
    return "auth/invalid-credentials";
  }
  if (status === 429) return "auth/rate-limited";
  if (error === "invalid_client" || error === "unauthorized_client") return "auth/keycloak-not-configured";
  return "auth/login-failed";
}

export function AuthProvider({
  children,
  keycloakUrl,
  clientId,
  impersonationDiscovery = true,
}: AuthProviderProps): React.ReactElement | null {
  const { t } = useLingui();
  const [state, setState] = useState<AuthState>({
    initialized: !!AUTH_DISABLED,
    authenticated: !!AUTH_DISABLED,
    user: AUTH_DISABLED ? DEV_USER : null,
    accessToken: AUTH_DISABLED ? "dev-token" : null,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  /**
   * Epoch-ms of the last successful access-token mint (`/auth/token` or
   * `/auth/refresh`). Drives the post-mint grace window in
   * handleSessionExpired: a `server-rejection` refresh-401 arriving within
   * GRACE_WINDOW_MS of a fresh mint is treated as a transient stale-cookie
   * race (defect D3, SPEC-USER-AUTH-RULE-013) rather than a revoked session.
   */
  const lastTokenMintedAtRef = useRef<number>(0);
  /** Latest access-token lifetime (sec), so the grace path can re-arm refresh. */
  const lastExpiresInRef = useRef<number>(0);
  // A code, translated at render so it follows the active locale.
  const [initError, setInitError] = useState<"init" | "restore" | null>(null);
  const [signInRefusal, setSignInRefusal] = useState<TenantRefusal | null>(null);

  /** Post-mint grace window (ms) for a transient refresh-401 — RULE-013. */
  const GRACE_WINDOW_MS = 5_000;

  // ---- Impersonation state (RULE-095) ---------------------------------------
  //
  // The descriptor is held TWICE on purpose: React state drives rendering, and
  // the ref is what the synchronous getters read, so a request issued in the
  // same tick as the arm is already stamped. Neither is ever persisted, and
  // NOTHING in the token lifecycle (refreshAccessToken, exchangeAuthCode,
  // loginWithCredentials) writes either of them - which is why a refresh tick
  // cannot revert the act-as mode.
  const [impersonation, setImpersonation] = useState<ImpersonationDescriptor | null>(null);
  const impersonationRef = useRef<ImpersonationDescriptor | null>(null);
  const [impersonationStatus, setImpersonationStatus] = useState<ImpersonationStatus>("none");
  const [impersonationTerminalCode, setImpersonationTerminalCode] = useState<
    ImpersonationEndCode | undefined
  >(undefined);
  const [impersonationExitFailure, setImpersonationExitFailure] =
    useState<ImpersonationExitFailure | null>(null);
  const [discoveryDone, setDiscoveryDone] = useState(false);
  const discoveryInFlightRef = useRef(false);
  const [permissionsEpoch, setPermissionsEpoch] = useState(0);
  // Optional on purpose: the provider must not throw when no QueryClientProvider
  // sits above it (unit tests); every SPA mounts one above AuthProvider.
  const queryClient = useContext(QueryClientContext);

  // ---- Token helpers wired into api/client --------------------------------

  const getAccessToken = useCallback(() => state.accessToken, [state.accessToken]);
  // A GETTER THAT FEEDS THE LANES, NOT A LANE (RULE-095(6)(B)): while a binding
  // is live it returns the TARGET's tenant from the descriptor, so no request
  // is ever stamped with the impersonator's tenant beside the target's act-as
  // reference (EPIC-IMPERSONATION-AC-002).
  const getTenantId = useCallback(
    () => impersonationRef.current?.target.tenant_id ?? state.user?.tenantId ?? null,
    [state.user],
  );

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!clientId) return null;

    const apiBase = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    // Network errors (DNS failure, timeout, offline) propagate as thrown
    // exceptions so the 401 interceptor in client.ts can distinguish them
    // from server-rejected refreshes (which return null).
    const response = await fetch(`${apiBase}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ client_id: clientId }),
    });

    if (!response.ok) {
      // The person's tenant is suspended or gone (R-MT-08): terminal, not a
      // revocation to retry - the listener below signs the person out.
      if (response.status === 403 || response.status === 410) {
        const refusal = tenantRefusalFromBody(await response.json().catch(() => null));
        if (refusal) {
          dispatchTenantRefused(refusal);
          return null;
        }
      }
      // 5xx (gateway unreachable / 503 from TASK-AUTH-REFRESH-503-SIGNAL) is a
      // TRANSIENT failure — surface it as a thrown exception so the network-error
      // retry path applies (RULE-013 bounded retry). A 429 (rate-limit /
      // brute-force protection) is likewise transient, not a revocation — the
      // gateway normally translates a Keycloak 429 to 503, but classify a raw
      // 429 reaching the refresher as transient too (defense-in-depth). A 4xx
      // (401 invalid_grant) is a server rejection — return null so the
      // grace/backoff policy decides.
      if (response.status >= 500 || response.status === 429) {
        throw new Error(`auth/refresh-unavailable:${response.status}`);
      }
      return null;
    }

    const data = (await response.json()) as {
      access_token: string;
      expires_in: number;
    };

    const user = extractUser(data.access_token);
    lastTokenMintedAtRef.current = Date.now();
    lastExpiresInRef.current = data.expires_in;
    setSignInRefusal(null);
    setState({
      initialized: true,
      authenticated: true,
      user,
      accessToken: data.access_token,
    });

    scheduleRefresh(data.expires_in);
    return data.access_token;
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Schedule token refresh before expiry --------------------------------

  const scheduleRefresh = useCallback(
    (expiresInSec: number) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      const delayMs = Math.max((expiresInSec - 60) * 1000, 10_000);
      refreshTimerRef.current = setTimeout(() => {
        void refreshAccessToken().catch(() => {
          refreshTimerRef.current = setTimeout(() => {
            void refreshAccessToken().catch(() => {});
          }, 10_000);
        });
      }, delayMs);
    },
    [refreshAccessToken],
  );

  // ---- Register token helpers with api/client ------------------------------

  useEffect(() => {
    setTokenGetter(getAccessToken);
    setTokenRefresher(refreshAccessToken);
    setTenantIdGetter(getTenantId);
  }, [getAccessToken, refreshAccessToken, getTenantId]);

  // The act-as getter (RULE-095(6)(B)). It reads the REF, so it is registered
  // once and is correct from the tick the binding arms until the tick it
  // clears; it returns null whenever no binding is live.
  useEffect(() => {
    setActAsGetter(() => impersonationRef.current?.session_ref ?? null);
    return () => setActAsGetter(() => null);
  }, []);

  // ---- Impersonation: local teardown -----------------------------------------

  /**
   * Clear every piece of local impersonation state: the descriptor, the query
   * cache, the hand-rolled workspace cache, the `?imp=` parameter and the hint.
   * Touches NO token and NO cookie.
   *
   * It used to also reset the source-UI bundle loader's module-scope maps. That
   * registry no longer exists - the whole source-plugin UI chain was deleted
   * under D-377 - so there is nothing left to clear here, and RULE-095(9) was
   * amended in the same change rather than left describing a no-op.
   */
  const clearLocalBinding = useCallback(() => {
    impersonationRef.current = null;
    setImpersonation(null);
    setImpersonationStatus("none");
    setImpersonationTerminalCode(undefined);
    queryClient?.clear();
    invalidateWorkspaceCache();
    // The exit-path ?imp= stripper (one of three, RULE-095(4)).
    window.history.replaceState(window.history.state, "", urlWithoutImpersonationHint());
    clearImpersonationHint();
  }, [queryClient]);

  /**
   * End any live binding FIRST on a teardown path (major finding M29): the
   * call is issued before the session is torn down, never refreshes or
   * retries, and its failure changes nothing - the server-side ends (idle,
   * expiry, the re-authentication void) are the fail-safe.
   */
  const endBindingForTeardown = useCallback((): Promise<void> | null => {
    const live = impersonationRef.current;
    if (!live) return null;
    const done = exitImpersonationSession(live.session_ref, { teardown: true })
      .then(() => undefined)
      .catch(() => undefined);
    clearLocalBinding();
    return done;
  }, [clearLocalBinding]);

  // ---- Session-expired listener (driven by client.ts 401 interceptor) -----

  useEffect(() => {
    if (AUTH_DISABLED) return;

    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const MAX_RETRIES = 3;

    function forceLogout() {
      // Both teardown paths end a live binding first (RULE-095(9)). This is
      // the path a lost refresh cookie actually takes.
      void endBindingForTeardown();
      retryCount = 0;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      sessionStorage.setItem(LOGGED_OUT_KEY, "1");
      setState({ initialized: true, authenticated: false, user: null, accessToken: null });
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'auth:session-expired' }, '*');
      }
    }

    function retryRefreshWithBackoff() {
      retryCount++;
      if (retryCount > MAX_RETRIES) {
        forceLogout();
        return;
      }
      const delay = Math.min(1000 * 2 ** (retryCount - 1), 10_000);
      retryTimer = setTimeout(() => {
        refreshAccessToken()
          .then((token) => {
            if (token) { retryCount = 0; return; }
            // A null result is a server-rejection (gateway 401 / invalid_grant),
            // NOT a terminal verdict on its own: the spec mandates bounded
            // backoff (MAX_RETRIES) before any hard logout, and a transient
            // stale-cookie race can clear within those attempts. Re-enter the
            // backoff loop; the `retryCount > MAX_RETRIES` guard at the top
            // forces forceLogout() once the bound is exhausted. This mirrors
            // the throw (5xx) path below, so both null AND throw retry ≤3.
            retryRefreshWithBackoff();
          })
          .catch(() => {
            retryRefreshWithBackoff();
          });
      }, delay);
    }

    function handleSessionExpired(e: Event) {
      const detail = (e as CustomEvent).detail as
        | { reason?: string; statusCode?: number; noRefreshToken?: boolean }
        | undefined;

      // --- network-error: gateway unreachable / 5xx — bounded retry ---------
      if (detail?.reason === 'network-error') {
        retryRefreshWithBackoff();
        return;
      }

      // --- "no refresh token" first-paint 401: no prior authenticated state.
      // This is the normal unauthenticated boot path (init tolerates it); the
      // 401-interceptor path must not escalate it to a logout (RULE-013).
      // Detected either from the gateway body (noRefreshToken) or from the
      // absence of any prior successful mint in this session.
      if (detail?.noRefreshToken || lastTokenMintedAtRef.current === 0) {
        return;
      }

      // --- server-rejection (gateway 401 / invalid_grant) -------------------
      // Post-mint grace window: a 401 arriving right after a successful mint is
      // a transient stale-cookie race, NOT a revoked session. Keep the
      // just-minted access token and re-arm refresh on its real lifetime.
      // The grace is bounded by the mint timestamp: a second `server-rejection`
      // 401 outside the 5s window of the LAST mint falls through to bounded
      // retry → logout, so the grace cannot re-arm indefinitely.
      const sinceMint = Date.now() - lastTokenMintedAtRef.current;
      if (
        lastTokenMintedAtRef.current > 0 &&
        sinceMint >= 0 &&
        sinceMint < GRACE_WINDOW_MS
      ) {
        if (lastExpiresInRef.current > 0) {
          scheduleRefresh(lastExpiresInRef.current);
        }
        return;
      }

      // Outside the grace window: bounded exponential backoff before any hard
      // logout (previously only `network-error` got this). Only after retries
      // are exhausted is the session concluded revoked → state-driven logout.
      retryRefreshWithBackoff();
    }

    // The person's tenant is suspended or gone (R-MT-08, api/tenantAccess):
    // sign out at once through the same teardown, and keep the reason for the
    // login page.
    function handleTenantRefused(e: Event) {
      const detail = (e as CustomEvent<TenantRefusedEventDetail>).detail;
      if (retryTimer) clearTimeout(retryTimer);
      forceLogout();
      if (detail?.refusal) setSignInRefusal(detail.refusal);
    }

    window.addEventListener('auth:session-expired', handleSessionExpired);
    window.addEventListener(TENANT_REFUSED_EVENT, handleTenantRefused);
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
      window.removeEventListener(TENANT_REFUSED_EVENT, handleTenantRefused);
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [refreshAccessToken, scheduleRefresh, endBindingForTeardown]);

  // ---- OIDC authorization code exchange with PKCE -------------------------

  const exchangeAuthCode = useCallback(
    async (code: string, overrideRedirectUri?: string, verifier?: string): Promise<boolean> => {
      if (!clientId) return false;

      // The silent session check passes its verifier; the interactive login's
      // survives its full-page redirect in sessionStorage.
      const codeVerifier = verifier ?? sessionStorage.getItem("verisa_pkce_verifier");

      try {
        const redirectUri =
          overrideRedirectUri ?? window.location.origin + window.location.pathname;
        const body: Record<string, string> = {
          grant_type: "authorization_code",
          client_id: clientId,
          code,
          redirect_uri: redirectUri,
        };
        if (codeVerifier) {
          body.code_verifier = codeVerifier;
        }

        // Route through auth proxy so the refresh token is set as an
        // HttpOnly cookie (F-1 / HLD-011 §7.4).
        const apiBase = import.meta.env.VITE_API_BASE_URL || "/api/v1";
        const response = await fetch(`${apiBase}/auth/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });

        if (!response.ok) {
          console.error("OIDC code exchange failed:", response.status);
          const refusal = tenantRefusalFromBody(await response.json().catch(() => null));
          if (refusal) setSignInRefusal(refusal);
          return false;
        }

        const data = (await response.json()) as {
          access_token: string;
          expires_in: number;
        };

        sessionStorage.removeItem("verisa_pkce_verifier");
        sessionStorage.removeItem(LOGGED_OUT_KEY);

        const user = extractUser(data.access_token);
        lastTokenMintedAtRef.current = Date.now();
        lastExpiresInRef.current = data.expires_in;
        setSignInRefusal(null);
        setState({
          initialized: true,
          authenticated: true,
          user,
          accessToken: data.access_token,
        });
        scheduleRefresh(data.expires_in);

        // The OIDC-callback ?imp= stripper (one of three, RULE-095(4)): the
        // rebuilt URL is origin + pathname, so neither ?code= nor ?imp=
        // survives. The hint itself was stashed at module scope and is
        // consumed by the arrival read, not here.
        window.history.replaceState(
          {},
          "",
          window.location.origin + window.location.pathname,
        );

        return true;
      } catch (err) {
        console.error("OIDC code exchange error:", err);
        sessionStorage.removeItem("verisa_pkce_verifier");
        return false;
      }
    },
    [clientId, scheduleRefresh],
  );

  // ---- Silent check-sso via hidden iframe ---------------------------------
  //
  // Requires /silent-check-sso.html at the app origin, which posts the
  // redirect URL back via window.parent.postMessage(location.href, origin).

  const attemptSilentSso = useCallback(async (): Promise<{ code: string; verifier: string } | null> => {
    if (!keycloakUrl || !clientId) return null;
    // The silent authorization request always carries a PKCE challenge. Without
    // SubtleCrypto (an insecure context) there is no challenge, so there is no
    // silent request: the user signs in from the login page.
    if (!hasSubtleCrypto()) return null;

    // The verifier lives in this closure only and is handed to the exchange
    // with the code; it is never written to storage.
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);

    return new Promise((resolve) => {
      const silentRedirectUri = `${window.location.origin}/silent-check-sso.html`;
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";

      let settled = false;
      const cleanup = (code: string | null) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        window.removeEventListener("message", onMessage);
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        resolve(code ? { code, verifier } : null);
      };

      // Timeout after 5 s — Keycloak is typically on LAN so this is generous
      const timer = setTimeout(() => cleanup(null), 5000);

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        const msg = typeof event.data === "string" ? event.data : null;
        if (!msg) return;
        try {
          const code = new URLSearchParams(new URL(msg).search).get("code");
          cleanup(code);
        } catch {
          cleanup(null);
        }
      };

      window.addEventListener("message", onMessage);

      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: silentRedirectUri,
        response_type: "code",
        scope: "openid profile email",
        prompt: "none",
        code_challenge: challenge,
        code_challenge_method: "S256",
      });
      iframe.src = `${keycloakUrl}/protocol/openid-connect/auth?${params.toString()}`;
      document.body.appendChild(iframe);
    });
  }, [keycloakUrl, clientId]);

  // ---- Initialize auth on mount -------------------------------------------

  useEffect(() => {
    if (AUTH_DISABLED) return;

    // Check for OIDC authorization code callback (post-redirect)
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (code) {
      void exchangeAuthCode(code).then((ok) => {
        if (!ok) {
          setState((prev) => ({ ...prev, initialized: true }));
        }
      });
      return;
    }

    // Skip session restore if user explicitly logged out (per-tab flag)
    if (sessionStorage.getItem(LOGGED_OUT_KEY)) {
      setState((prev) => ({ ...prev, initialized: true }));
      return;
    }

    // Try cookie-based refresh first (F-1). If the HttpOnly refresh cookie
    // exists, the backend will exchange it for a new access token without
    // needing a Keycloak iframe round-trip.
    void refreshAccessToken().then((token) => {
      if (token) return; // success — session restored

      // Fallback: attempt silent SSO via hidden iframe
      const silentRedirectUri = `${window.location.origin}/silent-check-sso.html`;
      void attemptSilentSso().then(async (silent) => {
        if (silent) {
          const ok = await exchangeAuthCode(silent.code, silentRedirectUri, silent.verifier);
          if (!ok) setState((prev) => ({ ...prev, initialized: true }));
        } else {
          setState((prev) => ({ ...prev, initialized: true }));
        }
      }).catch(() => {
        setState((prev) => ({ ...prev, initialized: true }));
        setInitError("init");
      });
    }).catch(() => {
      // Safety net (AC-020): prevents permanent white screen on init failure
      setState((prev) => ({ ...prev, initialized: true }));
      setInitError("restore");
    });

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Impersonation: the ORDERED arrival sequence (RULE-095(5)) ----------
  //
  // ENTRY AND RELOAD ARE ONE PATH (grill G3, D-IMP-07): both reach this read
  // after the ordinary bootstrap, and a reload simply re-discovers the live
  // binding - the `?imp=` hint only shortens an arrival, it is never needed.

  const discoveryActive = impersonationDiscovery && !AUTH_DISABLED;

  // The discovery signal for the raw-fetch lanes (TASK-IMP-BRANDING-UNDER-BINDING
  // CHG-006): an app that runs no discovery has nothing to wait for.
  useEffect(() => {
    if (!discoveryActive) setImpersonationDiscoveryState("settled");
  }, [discoveryActive]);

  const runDiscovery = useCallback(async () => {
    if (discoveryInFlightRef.current) return;
    discoveryInFlightRef.current = true;
    setImpersonationStatus("discovering");
    setImpersonationDiscoveryState("pending");
    let descriptor: ImpersonationDescriptor | null;
    try {
      descriptor = await fetchImpersonationDescriptor();
    } catch (err) {
      const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
      if (status === 404) {
        // The route is not served here: no binding can exist.
        descriptor = null;
      } else {
        // LOUD, never "no binding" (RULE-042): an absent banner would be
        // indistinguishable from "nothing is live".
        discoveryInFlightRef.current = false;
        setImpersonationStatus("error");
        setImpersonationDiscoveryState("error");
        return;
      }
    }
    // The arrival ?imp= stripper (one of three) - and the hint is consumed
    // exactly once, whatever the answer was.
    window.history.replaceState(window.history.state, "", urlWithoutImpersonationHint());
    clearImpersonationHint();
    if (descriptor) {
      // Set the state and arm the getter (the ref IS what the getter reads).
      impersonationRef.current = descriptor;
      setImpersonation(descriptor);
      // Clear the workspace selection BEFORE any subtree mounts (blocking
      // finding B2): the workspace feeding /me/permissions is a CLIENT-stamped
      // header, so a retained selection would resolve the TARGET's
      // permissions inside the IMPERSONATOR's workspace. WorkspaceProvider,
      // mounted fresh under the identity key, selects one the target belongs
      // to or renders the no-workspace empty state.
      setWorkspaceIdGetter(() => null);
      invalidateWorkspaceCache();
      queryClient?.clear();
      setImpersonationStatus("active");
    } else {
      setImpersonationStatus("none");
    }
    // Settled only now: the getter is armed (the ref above) or no binding exists.
    setImpersonationDiscoveryState("settled");
    discoveryInFlightRef.current = false;
    // ONLY NOW may the children - and PermissionProvider's first fetch - mount.
    setDiscoveryDone(true);
  }, [queryClient]);

  useEffect(() => {
    if (!discoveryActive || !state.authenticated || discoveryDone) return;
    void runDiscovery();
  }, [discoveryActive, state.authenticated, discoveryDone, runDiscovery]);

  const retryImpersonationDiscovery = useCallback(() => {
    void runDiscovery();
  }, [runDiscovery]);

  // ---- Impersonation: the terminal branch's receiver (RULE-095(7)) --------

  useEffect(() => {
    function onTerminal(e: Event) {
      if (!impersonationRef.current) return;
      const detail = (e as CustomEvent<ImpersonationTerminalEventDetail>).detail;
      if (detail?.reason === "impersonation_reauth") {
        // A RE-ARM AFTER A FULL RE-AUTHENTICATION IS REFUSED server-side (the
        // iat-versus-started_at end), and the client then renders the
        // ordinary signed-in state rather than a banner (CHG-007(h)).
        clearLocalBinding();
        return;
      }
      setImpersonationTerminalCode(detail ? TERMINAL_REASON_END_CODE[detail.reason] : undefined);
      setImpersonationStatus("terminal");
    }
    window.addEventListener(IMPERSONATION_TERMINAL_EVENT, onTerminal);
    return () => window.removeEventListener(IMPERSONATION_TERMINAL_EVENT, onTerminal);
  }, [clearLocalBinding]);

  // ---- Impersonation: the 60-second re-check poll (CHG-007(i)) ------------
  //
  // ONE read a minute while a binding is live - never per request or per
  // render. The read is stamped like any other request, so the server's own
  // per-request checks run on it and a terminal refusal reaches the branch
  // above. roles_recheck_ok = false means the server's role re-check is stale,
  // which is the cue to refetch permissions.
  useEffect(() => {
    if (impersonationStatus !== "active") return undefined;
    const id = setInterval(() => {
      void fetchImpersonationDescriptor()
        .then((next) => {
          const current = impersonationRef.current;
          if (!current) return;
          if (!next) {
            // Nothing live any more for this caller: the binding ended
            // somewhere else (another tab, an admin revoke already swept).
            setImpersonationTerminalCode(undefined);
            setImpersonationStatus("terminal");
            return;
          }
          impersonationRef.current = next;
          setImpersonation(next);
          if (!next.roles_recheck_ok) {
            setPermissionsEpoch((n) => n + 1);
          }
        })
        .catch(() => {
          // A terminal refusal was already dispatched by the interceptor; any
          // other failure is retried on the next tick.
        });
    }, IMPERSONATION_RECHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [impersonationStatus]);

  // Mirror the two layout facts other modules need (the banner offset, the
  // permission re-check cue). A layout effect, so the shells move under the
  // strip in the same paint the strip appears in.
  useLayoutEffect(() => {
    publishImpersonationShellSignal({
      bannerShown: Boolean(impersonation || impersonationExitFailure),
      permissionsEpoch,
    });
  }, [impersonation, impersonationExitFailure, permissionsEpoch]);
  useEffect(() => () => publishImpersonationShellSignal({ bannerShown: false, permissionsEpoch: 0 }), []);

  // ---- Impersonation: exit (RULE-095(9)) ----------------------------------

  const exitImpersonation = useCallback(async () => {
    const live = impersonationRef.current;
    if (!live || impersonationStatus === "ending") return;
    // Feedback within one second (RULE-045): the banner's exit turns busy.
    setImpersonationStatus("ending");
    let failed = false;
    try {
      await exitImpersonationSession(live.session_ref);
    } catch {
      failed = true;
    }
    // Local state is cleared EITHER WAY. NEVER logout(): it would set the
    // logged-out flag, clear the refresh cookie and hard-navigate to the
    // end-session endpoint, and the gateway's logout clears all three
    // per-client cookie names - signing the operator out of every SPA.
    clearLocalBinding();
    if (failed) {
      // Never a pretended success: a persistent notice says when it ends.
      setImpersonationExitFailure({ expiresAt: live.expires_at, returnTo: live.return_to });
      return;
    }
    const back = safeReturnTo(live.return_to);
    if (back) window.location.assign(back);
  }, [clearLocalBinding, impersonationStatus]);

  const returnFromImpersonation = useCallback(() => {
    const back = safeReturnTo(
      impersonationRef.current?.return_to ?? impersonationExitFailure?.returnTo,
    );
    clearLocalBinding();
    setImpersonationExitFailure(null);
    if (back) window.location.assign(back);
  }, [clearLocalBinding, impersonationExitFailure]);

  // ---- Actions ------------------------------------------------------------

  const login = useCallback(async () => {
    if (AUTH_DISABLED) {
      setState({
        initialized: true,
        authenticated: true,
        user: DEV_USER,
        accessToken: "dev-token",
      });
      return;
    }

    if (!keycloakUrl || !clientId) return;

    sessionStorage.removeItem(LOGGED_OUT_KEY);
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    // Store verifier in sessionStorage for the round-trip; cleared after exchange
    sessionStorage.setItem("verisa_pkce_verifier", verifier);

    const redirectUri = window.location.origin + window.location.pathname;
    const searchParams = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid profile email",
      code_challenge: challenge,
      code_challenge_method: "S256",
    });
    window.location.href = `${keycloakUrl}/protocol/openid-connect/auth?${searchParams.toString()}`;
  }, [keycloakUrl, clientId]);

  const loginWithCredentials = useCallback(
    async (username: string, password: string) => {
      if (AUTH_DISABLED) {
        setState({
          initialized: true,
          authenticated: true,
          user: DEV_USER,
          accessToken: "dev-token",
        });
        return;
      }

      if (!clientId) {
        // Stable, locale-agnostic error code — localized at the display site
        // (LoginPage) via useLingui().t against the active locale.
        throw new Error("auth/keycloak-not-configured");
      }

      // Use the auth proxy which sets the refresh token as an HttpOnly
      // cookie (F-1 / HLD-011 §7.4). The refresh token never touches JS.
      const apiBase = import.meta.env.VITE_API_BASE_URL || "/api/v1";
      const response = await fetch(`${apiBase}/auth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password, client_id: clientId }),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as Record<string, string | undefined>;
        // The raw (English) Keycloak / gateway text goes to the log only; the
        // thrown code is mapped to a localized message by LoginPage.
        console.warn("[AuthContext] token request refused:", response.status, body.error ?? "", body.error_description ?? "");
        throw new Error(loginErrorCode(response.status, body));
      }

      const data = (await response.json()) as {
        access_token: string;
        expires_in: number;
      };

      sessionStorage.removeItem(LOGGED_OUT_KEY);

      const user = extractUser(data.access_token);
      lastTokenMintedAtRef.current = Date.now();
      lastExpiresInRef.current = data.expires_in;
      setSignInRefusal(null);
      setState({
        initialized: true,
        authenticated: true,
        user,
        accessToken: data.access_token,
      });
      scheduleRefresh(data.expires_in);
    },
    [clientId, scheduleRefresh],
  );

  const logout = useCallback(() => {
    // Both teardown paths end a live binding FIRST (RULE-095(9)); the
    // end-session navigation below waits for that call to settle so the
    // browser does not abort it mid-flight.
    const bindingEnded = endBindingForTeardown();
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    sessionStorage.setItem(LOGGED_OUT_KEY, "1");
    setState({
      initialized: true,
      authenticated: false,
      user: null,
      accessToken: null,
    });
    // Clear HttpOnly refresh cookie via backend proxy.
    const apiBase = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    void fetch(`${apiBase}/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
    if (keycloakUrl && clientId) {
      const redirectUri = window.location.origin + (import.meta.env.BASE_URL || "/");
      const endSessionUrl = `${keycloakUrl}/protocol/openid-connect/logout?client_id=${clientId}&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
      if (bindingEnded === null) {
        window.location.href = endSessionUrl;
      } else {
        void bindingEnded.then(() => {
          window.location.href = endSessionUrl;
        });
      }
    }
  }, [keycloakUrl, clientId, endBindingForTeardown]);

  // THE EFFECTIVE IDENTITY (CHG-007(e)): while a binding is live the shell
  // renders the TARGET from the descriptor, never extractUser(token) - the
  // token is, and stays, the impersonator's own.
  const effectiveUser = useMemo<AuthUser | null>(() => {
    if (!impersonation) return state.user;
    const target = impersonation.target;
    return {
      id: target.user_id,
      tenantId: target.tenant_id,
      email: "",
      name: target.display_name || target.user_id,
      roles: target.highest_role ? [target.highest_role] : [],
    };
  }, [impersonation, state.user]);

  const hasRole = useCallback(
    (role: string) => {
      if (AUTH_DISABLED) return true;
      return effectiveUser?.roles.includes(role) ?? false;
    },
    [effectiveUser],
  );

  const hasAnyRole = useCallback(
    (roles: string[]) => {
      if (AUTH_DISABLED) return true;
      return roles.some((r) => effectiveUser?.roles.includes(r));
    },
    [effectiveUser],
  );

  // ---- Render -------------------------------------------------------------

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      user: effectiveUser,
      login,
      loginWithCredentials,
      logout,
      hasRole,
      hasAnyRole,
      ssoEnabled: SSO_ENABLED,
      signInRefusal,
      impersonation,
      impersonator: impersonation ? state.user : null,
      impersonationStatus,
      impersonationTerminalCode,
      impersonationExitFailure,
      permissionsEpoch,
      exitImpersonation,
      returnFromImpersonation,
      retryImpersonationDiscovery,
    }),
    [
      state,
      effectiveUser,
      login,
      loginWithCredentials,
      logout,
      hasRole,
      hasAnyRole,
      signInRefusal,
      impersonation,
      impersonationStatus,
      impersonationTerminalCode,
      impersonationExitFailure,
      permissionsEpoch,
      exitImpersonation,
      returnFromImpersonation,
      retryImpersonationDiscovery,
    ],
  );

  if (!state.initialized) {
    return null;
  }

  if (initError) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', gap: '16px',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <p style={{ color: '#d32f2f', margin: 0 }}>
          {initError === "init"
            ? t`Failed to initialize authentication.`
            : t`Failed to restore session. Please try again.`}
        </p>
        <button
          onClick={() => { setInitError(null); window.location.reload(); }}
          style={{ padding: '8px 16px', cursor: 'pointer' }}
        >
          {t`Retry`}
        </button>
      </div>
    );
  }

  // The children mount only once the arrival read has answered, so the FIRST
  // permissions request is already stamped (RULE-095(5)). Unauthenticated
  // children (the login surfaces) are never held back.
  const holdChildren = discoveryActive && state.authenticated && !discoveryDone;

  return (
    <AuthContext.Provider value={value}>
      {holdChildren ? <ImpersonationArrival /> : children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Impersonation UI slots (mounted by the SPA shells, OUTSIDE every gate)
// ---------------------------------------------------------------------------

/**
 * The re-check poll's cadence in ms. DERIVATION: SPEC-FRONTEND-RULE-095 and
 * the TASK name a 60-second re-check; the server's role re-check budget is
 * far coarser, so a minute is fast enough to surface a revoke that happened
 * without traffic and cheap enough to be one indexed read per minute.
 */
const IMPERSONATION_RECHECK_INTERVAL_MS = 60_000;

/** Clears the fixed strip for a full-page state rendered under it. */
const belowStripSx: SxProps<Theme> = {
  pt: `${IMPERSONATION_BANNER_MIN_HEIGHT}px`,
};

/**
 * What renders while the arrival read is held: nothing on an ordinary boot
 * (the same blank the provider shows while initializing), the banner's loading
 * strip when an arrival hint says a binding is expected, and a LOUD error with
 * retry when the read failed - never the app with an unknown binding state.
 */
function ImpersonationArrival(): React.ReactElement | null {
  const ctx = useContext(AuthContext);
  const { t } = useLingui();
  if (!ctx) return null;
  if (ctx.impersonationStatus === "error") {
    return (
      <Box sx={belowStripSx} data-testid="impersonation-arrival-error">
        <ImpersonationBanner binding={null} state="error" onExit={noop} onReturn={noop} />
        <EmptyState
          variant="error"
          title={t`Could not check for an active view`}
          description={t`The page cannot load until this check succeeds.`}
          actionText={t`Retry`}
          onAction={ctx.retryImpersonationDiscovery}
        />
      </Box>
    );
  }
  if (readImpersonationHint()) {
    return <ImpersonationBanner binding={null} state="loading" onExit={noop} onReturn={noop} />;
  }
  return null;
}

function noop(): void {
  /* the loading and error strips carry no binding to exit */
}

export default AuthContext;
